import { useMemo, useState } from "react";

import { GameFullResponse } from "../../types/gameFull";
import {
  getPitchesForPA,
  getRecentHalfInningKeys,
  getResultingState,
  groupHistoryByHalfInning,
} from "../../utils/plateAppearanceFeed";
import { FeedCard, FeedSectionLabel } from "./FeedCard";
import { HalfInningHeader } from "./HalfInningHeader";
import { PitchList, PitchListProps } from "./PitchRow";
import { PlateAppearanceHeader } from "./PlateAppearanceHeader";

type GameHistoryProps = PitchListProps & {
  data: GameFullResponse;
  awayTeamColor: string;
  homeTeamColor: string;
  selectedPlateAppearanceId?: string;
  onMatchupClick?: (plateAppearanceId: string) => void;
};

export const GameHistory = ({
  data,
  awayTeamColor,
  homeTeamColor,
  selectedPlateAppearanceId,
  onMatchupClick,
  ...pitchListProps
}: GameHistoryProps) => {
  const groups = useMemo(() => groupHistoryByHalfInning(data), [data]);
  const recentKeys = getRecentHalfInningKeys(data.game);
  const selectedPitchPA = data.activities.find(
    (a) => a.activityId === pitchListProps.selectedActivityId,
  )?.plateAppearanceId;

  // Half-innings the user opened or closed. The rest follow the default, so
  // older ones close as the game moves on and a long game stays light.
  const [toggled, setToggled] = useState<ReadonlyMap<string, boolean>>(
    () => new Map(),
  );

  if (groups.length === 0) return null;

  return (
    <section
      data-testid="plate-appearance-feed-history-panel"
      className="space-y-3"
    >
      <FeedSectionLabel>Game History</FeedSectionLabel>

      {groups.map((group) => {
        const hasSelection = group.plateAppearances.some(
          ({ plateAppearanceId }) =>
            plateAppearanceId === selectedPlateAppearanceId ||
            plateAppearanceId === selectedPitchPA,
        );
        const isExpanded =
          toggled.get(group.key) ?? (recentKeys.has(group.key) || hasSelection);

        return (
          <FeedCard
            key={group.key}
            data-testid="plate-appearance-feed-half-inning-item"
            data-inning={group.inning}
            data-half={group.isTopOfInning ? "top" : "bottom"}
            aria-expanded={isExpanded}
          >
            <HalfInningHeader
              data-testid="plate-appearance-feed-half-inning-value"
              toggleTestId="plate-appearance-feed-half-inning-toggle-button"
              inning={group.inning}
              isTopOfInning={group.isTopOfInning}
              isExpanded={isExpanded}
              onToggle={() =>
                setToggled((prev) => new Map(prev).set(group.key, !isExpanded))
              }
            />

            {isExpanded &&
              group.plateAppearances.map(({ plateAppearanceId, ...pa }) => (
                <div
                  key={plateAppearanceId}
                  data-testid="plate-appearance-item"
                  data-plate-appearance-id={plateAppearanceId}
                >
                  <PlateAppearanceHeader
                    data-testid="plate-appearance-header-button"
                    data-plate-appearance-id={plateAppearanceId}
                    pitcherName={pa.pitcher.playerName}
                    hitterName={pa.hitter.playerName}
                    pitcherId={pa.pitcher.playerId}
                    hitterId={pa.hitter.playerId}
                    isTopOfInning={pa.isTopOfInning}
                    resultingState={getResultingState(data, plateAppearanceId)}
                    awayTeamColor={awayTeamColor}
                    homeTeamColor={homeTeamColor}
                    isSelected={plateAppearanceId === selectedPlateAppearanceId}
                    onClick={
                      onMatchupClick
                        ? () => onMatchupClick(plateAppearanceId)
                        : undefined
                    }
                  />
                  <PitchList
                    pitches={getPitchesForPA(data, plateAppearanceId)}
                    {...pitchListProps}
                  />
                </div>
              ))}
          </FeedCard>
        );
      })}
    </section>
  );
};
